import { PermissionType } from '@/graphql/generated/types.generated';
import { AddOnRow } from './rows/AddOnRow';
import { BooleanAddOnRow } from './rows/BooleanAddOnRow';
import { EnumAddOnRow } from './rows/EnumAddOnRow';
import { FeatureRow } from './rows/FeatureRow';
import type { MappedFeature } from './utils/map-graphql-to-calculator.util';

interface FeatureAddonRowProps {
    feature: MappedFeature;
    currency?: { symbol: string } | null;
    featureByCode: Record<string, MappedFeature>;
    addOnQty: Record<string, number>;
    addOnBooleans: Record<string, boolean>;
    addOnEnums: Record<string, string>;
    onNumericChange: (key: string, qty: number) => void;
    onBoolChange: (key: string, enabled: boolean) => void;
    onEnumChange: (key: string, value: string) => void;
}

function isParentActive(
    parentCode: string,
    featureByCode: Record<string, MappedFeature>,
    addOnQty: Record<string, number>,
    addOnBooleans: Record<string, boolean>,
    addOnEnums: Record<string, string>,
): boolean {
    const parent = featureByCode[parentCode];
    if (!parent) return false;

    switch (parent.type) {
        case PermissionType.Boolean:
            return parent.value === 1 || (addOnBooleans[parentCode] ?? false);
        case PermissionType.Numeric:
            return parent.value > 0 || (addOnQty[parentCode] ?? 0) > 0;
        case PermissionType.Enum:
            return parent.displayValue !== '' || (addOnEnums[parentCode] ?? '') !== '';
        default:
            return false;
    }
}

/**
 * Dispatches to the correct interactive row component based on the feature's
 * type and available add-on metadata. Falls back to the read-only FeatureRow.
 * When the feature has a `dependsOn`, its controls are disabled until the
 * parent permission is active (included in the plan or added as an add-on).
 */
export const FeatureAddonRow = ({
    feature,
    currency,
    featureByCode,
    addOnQty,
    addOnBooleans,
    addOnEnums,
    onNumericChange,
    onBoolChange,
    onEnumChange,
}: FeatureAddonRowProps) => {
    // The parent is active when it's granted by the selected plan (base-plan-only parents like
    // `dashboards` are filtered out of the add-on list, so this is the only signal for them) or
    // when it's been added dynamically as an add-on in this session.
    const parentActive =
        feature.dependsOn == null ||
        feature.dependsOnIncludedInPlan === true ||
        isParentActive(feature.dependsOn, featureByCode, addOnQty, addOnBooleans, addOnEnums);
    const disabled = !parentActive;

    if (
        feature.type === PermissionType.Numeric &&
        (feature.addOnPricePerUnit !== undefined || (feature.addOnTiers?.length ?? 0) > 0)
    ) {
        return (
            <AddOnRow
                feature={feature}
                extraQty={addOnQty[feature.code] ?? 0}
                currency={currency}
                onChange={onNumericChange}
                disabled={disabled}
            />
        );
    }

    if (feature.type === PermissionType.Boolean && feature.addOnPrice !== undefined) {
        return (
            <BooleanAddOnRow
                feature={feature}
                enabled={addOnBooleans[feature.code] ?? false}
                currency={currency}
                onChange={onBoolChange}
                disabled={disabled}
            />
        );
    }

    if (feature.type === PermissionType.Enum && feature.addOnOptions !== undefined) {
        return (
            <EnumAddOnRow
                feature={feature}
                selectedValue={addOnEnums[feature.code] ?? feature.displayValue}
                currency={currency}
                onChange={onEnumChange}
                disabled={disabled}
            />
        );
    }

    return <FeatureRow feature={feature} />;
};
